import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Typography } from '@mui/material';
import { useFormikContext } from 'formik';
import { v4 as uuid } from 'uuid';

import { debounce } from '@altirllc/shared/utils/functionalUtils';

import { handleError } from '@/utils/errorUtils';

import { LocationPin } from '@/components/icons/LocationPin';
import { Map } from '@/components/Map';
import { formatAddress } from '@/components/placesAutosuggest/utils';

import type { TAddress } from '@/types/common';

import { STATES } from '@/mocks/states';
import { ensureGoogleMaps } from '@/scripts/googleMaps';

import { styles } from './InteractiveMap.styles';

type TFormDataExt = {
  location: TAddress & { bounds: google.maps.LatLngBounds };
};

// Roughly the center of the USA (picked manually)
const DEFAULT_LAT = 40;
const DEFAULT_LNG = -101;

const didPositionChange = (
  { lat, lon }: { lat: number; lon: number },
  coords2: google.maps.LatLng,
  precision: number,
) => Math.sqrt((coords2.lat() - lat) ** 2 + (coords2.lng() - lon) ** 2) > precision;

const preparePosition = (position: google.maps.LatLng) => ({
  lat: position.lat(),
  lon: position.lng(),
});

export const InteractiveMap = () => {
  const { t } = useTranslation();
  const { values, setFieldValue } = useFormikContext<TFormDataExt>();
  const { lat, lon, bounds } = values.location ?? {};

  const updateAddress = useMemo(() => {
    let relevantRequestId: string;

    return debounce(async (location: google.maps.LatLng) => {
      const position = preparePosition(location);

      setFieldValue('location.lat', position.lat);
      setFieldValue('location.lon', position.lon);

      await ensureGoogleMaps();

      relevantRequestId = uuid();

      const requestId = relevantRequestId;
      const geocoder = new google.maps.Geocoder();
      const places = await geocoder.geocode({ location, region: 'US' });
      const suitable = places.results.find(
        ({ types }) => types.includes('street_address') || types.includes('premise'),
      );

      if (suitable && requestId === relevantRequestId) {
        const { address, zip, state, ...rest } = formatAddress(suitable);
        const { formatted_address: formatted } = suitable;

        setFieldValue('location', {
          addressLine1: address,
          addressLine2: '',
          zipCode: zip,
          formatted,
          state,
          stateFullName: STATES[state as keyof typeof STATES],
          ...rest,
          ...position,
        });
      }
    }, 300);
  }, [setFieldValue]);

  const onCenterChange = (map: google.maps.Map) => {
    const center = map.getCenter();

    if (center && didPositionChange({ lat, lon }, center, 0.0002)) {
      updateAddress(center).catch((error) => handleError(error, 'UNHANDLED_RESPONSE'));
    }
  };

  return (
    <>
      <Typography variant="caption" color="textSecondary" align="center" component="p">
        {t('stationSetup.generalInfo.moveMapToChange')}
      </Typography>

      <Box sx={styles.mapWrapper}>
        <Map
          sx={styles.map}
          onCenterChange={onCenterChange}
          lat={lat ?? DEFAULT_LAT}
          lng={lon ?? DEFAULT_LNG}
          fitBounds={bounds}
          disableDefaultUI
          zoom={3}
        />

        {lat != null && lon != null && <LocationPin sx={styles.pin} />}
      </Box>
    </>
  );
};
